import WRAP from "WRAP-JS";
import { mps2kt, parseUTCDate } from "../../../utils/FPEUtils";
import { WX_ID_AMEDAS } from "../../../store/WeatherContentsState";
import {
  configAmedasAirTempLayer,
  configAmedasDh,
  configAmedasPrecipitationLayer,
  configAmedasSnowDepthLayer,
  configAmedasSunshineLayer,
  configAmedasWindDirectionLayer, configAmedasWindSpeedLayer
} from "./config/ConfigAmedas";
import LegendAmedas from "../legend/LegendAmedas";
import { nearValidTime } from "../helper/MapUtil";
import TimeController from "../helper/TimeController";
import MissingDataObserver from "../helper/MissingDataObserver";
import { COMMUNICATE_ERROR_MESSAGE_AMEDAS, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD } from "../../../constants/ErrorMessage";

export const AMEDAS_TYPE_AIR_TEMP = 'AIR_TEMP';
export const AMEDAS_TYPE_SUNSHINE = 'SUNSHINE';
export const AMEDAS_TYPE_PRECIPITATION = 'PRECIPITATION';
export const AMEDAS_TYPE_SNOW_DEPTH = 'SNOW_DEPTH';
export const AMEDAS_TYPE_WIND_DIR = 'WIND_DIR';

export const WHOLE_COUNTRY = "WHOLE_COUNTRY";
export const HOKKAIDO = "HOKKAIDO";
export const TOHOKU = "TOHOKU";
export const KANTO = "KANTO";
export const CHUBU = "CHUBU";
export const KINKI = "KINKI";
export const CHUGOKU = "CHUGOKU";
export const SHIKOKU = "SHIKOKU";
export const KYUSHU = "KYUSHU";

const AREA = {
  [HOKKAIDO]:["11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "24"],
  [TOHOKU]:["31", "32", "33", "34", "35", "36"],
  [KANTO]:["40", "41", "42", "43", "44", "45", "46"],
  [CHUBU]:["48", "49", "50", "51", "52", "53", "54", "55", "56", "57"],
  [KINKI]:["60", "61", "62", "63", "64", "65"],
  [CHUGOKU]:["66", "67", "68", "69", "81",],
  [SHIKOKU]:["71", "72", "73", "74"],
  [KYUSHU]:["82", "83", "84", "85", "86", "87", "88", "91", "92", "93", "94"]
}

class Amedas {

  static NAME = WX_ID_AMEDAS;
  static AREA_VISIBLE_STATE_DEFAULT = {
    [HOKKAIDO]: true,
    [TOHOKU]: true,
    [KANTO]: true,
    [CHUBU]: true,
    [KINKI]: true,
    [CHUGOKU]: true,
    [SHIKOKU]: true,
    [KYUSHU]: true
  };
  areaVisibleState = {...Amedas.AREA_VISIBLE_STATE_DEFAULT};
  wholeCountry;
  areaVisibleIds;

  dataType;
  dh;
  layerAirTemp;
  layerSunshine;
  layerPrecipitation;
  layerSnowDepth;
  layerWindDirection;
  layerWindSpeed;

  ready = false;
  dhAutoUpdate = true;
  visible = false;
  dispatcher;
  validTime = '---';

  windDirectionVisible;
  windSpeedVisible;

  // タイムコントローラ
  amedasTimeController;
  // タイムコントローラのリスト
  timeList;
  snowTimeList;
  // 自動更新フラグ
  autoRefresh = true;
  errorMessageInfo;

  constructor(config) {
    this.amedasTimeController = new TimeController(WX_ID_AMEDAS, {
      label: 'AMeDAS',
      getTimeList: () => {
        if (this.timeList) {
          return this.timeList;
        }
        this._updateTimeList();
        return this.timeList;
      },
      updated: () => {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      },
      changeAutoRefresh: (autoFlg) => {
        this.setAutoRefresh(autoFlg);
      }
    });
    this.windDirectionVisible = !!config.windSpdDir;
    this.windSpeedVisible = !!config.windSpdDir;
    this.dataType = config.defaultElement || AMEDAS_TYPE_AIR_TEMP;
    this.layerAirTemp = new WRAP.Geo.Layer(`${Amedas.NAME}_AIR_TEMP`);
    this.layerSunshine = new WRAP.Geo.Layer(`${Amedas.NAME}_SUNSHINE`);
    this.layerPrecipitation = new WRAP.Geo.Layer(`${Amedas.NAME}_PRECIPITATION`);
    this.layerSnowDepth = new WRAP.Geo.Layer(`${Amedas.NAME}_SNOW_DEPTH`);
    this.layerWindDirection = new WRAP.Geo.Layer(`${Amedas.NAME}_WIND_DIR`);
    this.layerWindSpeed = new WRAP.Geo.Layer(`${Amedas.NAME}_WIND_SPEED`);
    this.layerAirTemp.setVisible(false);
    this.layerSunshine.setVisible(false);
    this.layerPrecipitation.setVisible(false);
    this.layerSnowDepth.setVisible(false);
    this.layerWindDirection.setVisible(false);
    this.layerWindSpeed.setVisible(false);
    this.layerWindDirection.disableInteraction(true);
    this.layerWindSpeed.disableInteraction(true);
    this.areaVisibleIds = this._getVisibleAreaIds(this.areaVisibleState);
    this.wholeCountry = this._isWholeCountry(this.areaVisibleState);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
    this.missingDataObserver = new MissingDataObserver(this.dispatcher, {
      [WX_ID_AMEDAS]: () => (
        this.dataType === AMEDAS_TYPE_SNOW_DEPTH ? 7200 : 1800
      )
    }, this);
  }

  setAmedasElementInfoDispatcher(dispatcher) {
    this.amedasDispatcher = dispatcher;
  }

  async initialize(visible) {
    await this.initData();
    this.initLayer();
    this.initTimeController(visible);
    this.ready = true;
  }

  initData() {
    return new Promise(resolve => {
      this.dh = new WRAP.Data(configAmedasDh);
      this.dh.validate((displayTime, data, content) => {
        return this._validateTime(data, displayTime, content);
      });
      this.missingDataObserver.monitoring(WX_ID_AMEDAS, this.dh, this._getTargetLayer.bind(this));

      this.dh.updated('timelist', ref => {
        if (this.errorMessageInfo) {
          this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo});
          this.errorMessageInfo = null;
        }
        this._selectValidTime(ref, validTime => {
          this.dh.load('time', {validtime: validTime}, true, (ref) => {
            resolve(ref);
          }, () => {
            this.dispatcher({type: 'SHOW_ERROR', payload: {
              message: COMMUNICATE_ERROR_MESSAGE_AMEDAS,
              replacement: {data: 'time'},
              additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
            }});
          });
        });
      }, () => {
        this.errorMessageInfo = this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_AMEDAS,
          replacement: {data: 'timelist'},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }});
      });

      this.dh.updated('data', data => {
        this.dhORGData = JSON.parse(JSON.stringify(data));
        this._filterWRAPData(data);
      });

      this.dh.updated('master', null, () => {
        this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_AMEDAS,
          replacement: {data: 'master'},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
        }});
      });
      this.dh.updated('info', null, () => {
        this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_AMEDAS,
          replacement: {data: 'info'},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
        }});
      });
    });
  }

  initLayer() {
    this.layerAirTemp.configure(this.dh, configAmedasAirTempLayer);
    this.layerSunshine.configure(this.dh, configAmedasSunshineLayer);
    this.layerPrecipitation.configure(this.dh, configAmedasPrecipitationLayer);
    this.layerSnowDepth.configure(this.dh, configAmedasSnowDepthLayer);
    this.layerWindDirection.configure(this.dh, configAmedasWindDirectionLayer);
    this.layerWindSpeed.configure(this.dh, configAmedasWindSpeedLayer);

    //正時を表示しているときのみ、ツールチップに積雪の情報を表示するためのフィルタ処理
    const snowTooltipFilter = (key, prop, i, rows) => {
      //積雪がフィルタリングされるときは直前の無駄な改行もフィルタ
      const _key = key === "" ? rows[i + 1] : key;
      const isSnow = ["%label.SNWDPT%",
        "%label.SNWFLL_01HOUR%",
        "%label.SNWFLL_03HOUR%",
        "%label.SNWFLL_06HOUR%",
        "%label.SNWFLL_12HOUR%",
        "%label.SNWFLL_24HOUR%"].includes(_key);

      if (isSnow) {
        const utc = parseUTCDate(prop.valid_time);
        // 正時のみにフィルタリング
        if (utc.getMinutes() !== 0 || utc.getSeconds() !== 0) {
          return false;
        }
      }
      return true;
    }

    this.layerAirTemp.setAttributes({contentFilter: snowTooltipFilter});
    this.layerSunshine.setAttributes({contentFilter: snowTooltipFilter});
    this.layerPrecipitation.setAttributes({contentFilter: snowTooltipFilter});

    const filter = (feature) => {
      const windSpdMsec = feature.properties.WNDSPD;
      if (windSpdMsec < 0 || isNaN(windSpdMsec)) {
        return false;
      }
      if (isNaN(feature.properties.WNDDIR)){
        return false;
      }
      feature.properties.WNDSPDKT = Math.round(mps2kt(windSpdMsec));
      feature.properties.WNDSPDKTTEXT = `${feature.properties.WNDSPDKT}kt`
      return true;
    };
    this.layerWindDirection.setAttributes({
      filter: filter
    });
    this.layerWindSpeed.setAttributes({
      filter: filter
    });
    if (this.windDirectionVisible) {
      this.layerWindDirection.show();
    }
    if (this.windSpeedVisible) {
      this.layerWindSpeed.show();
    }
  }

  async drawLayer(visible) {
    if (!this.ready) {
      await this.initialize(visible);
    }
    this.setVisible(visible);
  }

  getLayer() {
    return [this.layerWindDirection, this.layerWindSpeed, this.layerAirTemp, this.layerSunshine, this.layerPrecipitation, this.layerSnowDepth];
  }

  setVisible(visible) {
    this.visible = visible;
    if (!this.ready) return;

    const targetLayer = this._getTargetLayer();

    if (visible) {
      targetLayer.show();
      if (this.windDirectionVisible) {
        this.layerWindDirection.show();
      }
      if (this.windSpeedVisible) {
        this.layerWindSpeed.show();
      }
    } else {
      targetLayer.hide();
      this.layerWindDirection.hide();
      this.layerWindSpeed.hide();
    }

    const enabled = visible && this.autoRefresh;
    if (this.dhAutoUpdate !== enabled) {
      this.dh.setAutoUpdate('timelist', enabled);
      this.dhAutoUpdate = enabled;
    }

    this._updateTimeList();

    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});

  }

  initTimeController(visible) {
    this.amedasTimeController.addLayer(this.layerAirTemp);
    this.amedasTimeController.addLayer(this.layerSunshine);
    this.amedasTimeController.addLayer(this.layerPrecipitation);
    this.amedasTimeController.addLayer(this.layerSnowDepth);
    this.amedasTimeController.addLayer(this.layerWindDirection);
    this.amedasTimeController.addLayer(this.layerWindSpeed);
    if (visible) {
      this.amedasTimeController.setDisplayTime(this.validTime);
    }
  }
  getTimeController() {
    return this.amedasTimeController;
  }

  setAutoRefresh(autoFlg) {
    if (autoFlg === this.autoRefresh) return;
    this.autoRefresh = autoFlg;
    if (!this.ready) return;
    this.dh.setAutoUpdate('timelist', this.autoRefresh);
    this.dhAutoUpdate = this.autoRefresh;
  }

  _updateTimeList() {

    const validTimes = this.dh.retained.timelist?.validtime;
    if (!validTimes) return;

    if (this.dataType !== AMEDAS_TYPE_SNOW_DEPTH) {
      this.timeList = Array.from(validTimes).sort();
    } else {
      this.timeList = Array.from(validTimes).sort().filter(time => {
        return new Date(time).getMinutes() === 0;
      });
    }

    if (this.autoRefresh) {
      this.amedasTimeController.setCurrentTime(this.currentTime);
    }

    this.amedasTimeController.adjustCurrentTime();
    this.amedasTimeController.invalidate();
  }

  _validateTime(data, displayTime, content) {

    let vt;
    if(this.dataType !== AMEDAS_TYPE_SNOW_DEPTH) {
      vt = nearValidTime(data.retained.timelist.validtime, displayTime);
    } else {
      vt = nearValidTime(data.retained.timelist.validtime, displayTime, {onTheHour: true});
    }
    if (vt) {
      this.validTime = content.validtime = vt;
      this.dh.controller.loadTime(this.dh, this.validTime);
      return true;
    }
    return false;
  }

  changeRoutePointEditing(routePointEditing) {
    const layer = this._getTargetLayer();
    if (layer === this.layerWindDirection || layer === this.layerWindSpeed) {
      return;
    }
    layer.disableInteraction(routePointEditing);
  }

  getTimeInformation() {
    return {
      id: Amedas.NAME,
      label: 'AMeDAS',
      baseTime: {
        id: Amedas.NAME,
        time: this.validTime
      }
    }
  }

  getLegend() {
    return {
      id: Amedas.NAME,
      label: 'AMeDAS',
      component: LegendAmedas,
      contents: this
    };
  }

  getDataType() {
    return this.dataType;
  }

  setDataType(dataType) {
    if (dataType === this.dataType) return;

    //現在のdataTypeを非表示
    this.setVisible(false);
    this.dataType = dataType;
    //切り替え後のdataTypeを表示
    this.setVisible(true);
    this.amedasDispatcher(dataType);
  }

  getWindDirectionVisible() {
    return this.windDirectionVisible;
  }

  getWindSpeedVisible() {
    return this.windSpeedVisible;
  }

  setWindDirectionVisible(visible) {
    if (visible === this.windDirectionVisible) return;

    this.windDirectionVisible = visible;
    if (this.windDirectionVisible) {
      this.layerWindDirection.show();
    } else {
      this.layerWindDirection.hide();
    }
  }

  setWindSpeedVisible(visible) {
    if (visible === this.windSpeedVisible) return;

    this.windSpeedVisible = visible;
    if (this.windSpeedVisible) {
      this.layerWindSpeed.show();
    } else {
      this.layerWindSpeed.hide();
    }
  }

  getAreaState() {
    return this.areaVisibleState;
  }

  setAreaState(areaState) {
    this.areaVisibleState = areaState;
    this.areaVisibleIds = this._getVisibleAreaIds(this.areaVisibleState);
    this.wholeCountry = this._isWholeCountry(this.areaVisibleState);
    if (!this.ready) return;
    this.dh.update('data', this.dhORGData);
  }

  resetAreaState() {
    this.areaVisibleState = {...Amedas.AREA_VISIBLE_STATE_DEFAULT};
    this.areaVisibleIds = this._getVisibleAreaIds(this.areaVisibleState);
    this.wholeCountry = this._isWholeCountry(this.areaVisibleState);
    if (!this.ready) return;
    this.dh.update('data', this.dhORGData);
  }

  _filterWRAPData(data) {
    if (this.wholeCountry) {
      return;
    }
    //AREAフィルタ
    data.features = data.features.filter(feature => {
      const id = feature.properties.RAWID.substring(0, 2);
      return this.areaVisibleIds.includes(id);
    });
  }

  _selectValidTime(ref, cb) {
    let targetTime;
    if (!this.autoRefresh) {
      targetTime = this.amedasTimeController.getCurrentTime();
    } else {
      targetTime = Date.now();
    }

    let vt;
    this.currentTime = vt = nearValidTime(ref.validtime, targetTime);
    if (this.dataType === AMEDAS_TYPE_SNOW_DEPTH) {
      vt = nearValidTime(ref.validtime, targetTime, {onTheHour:true});
    }

    if (this.validTime !== vt) {
      this.validTime = vt;
      cb(vt);
    }
    this._updateTimeList();
    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  _getTargetLayer() {
    switch (this.dataType) {
      case AMEDAS_TYPE_AIR_TEMP:
        return this.layerAirTemp;
      case AMEDAS_TYPE_SUNSHINE:
        return this.layerSunshine;
      case AMEDAS_TYPE_PRECIPITATION:
        return this.layerPrecipitation;
      case AMEDAS_TYPE_SNOW_DEPTH:
        return this.layerSnowDepth;
      case AMEDAS_TYPE_WIND_DIR:
        return this.layerWindDirection;
      default:
    }
  }

  _getVisibleAreaIds(areaVisibleState) {
    const ids = Object.entries(areaVisibleState).reduce( (accumulator, [area, visible]) => {
      if (visible) {
        accumulator = accumulator.concat(AREA[area]);
      }
      return accumulator;
    }, []);
    return ids;
  }

  _isWholeCountry(areaVisibleState) {
    return Object.values(areaVisibleState).every(v=>{return v});
  }
}

export default Amedas;
